/**
 * useOnlineStatus — React hook for detecting network connectivity.
 *
 * Uses the browser's navigator.onLine + online/offline events.
 * Also performs a lightweight "ping" to the API server to distinguish
 * between "browser says online but server is unreachable" scenarios.
 *
 * Returns:
 *   - isOnline: boolean — whether the browser reports being online
 *   - isServerReachable: boolean — whether we can actually reach the API
 *   - wasOffline: boolean — true if we just came back online (for showing "reconnected" toast)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE } from "../utils/apiClient";

interface OnlineStatus {
  /** Browser's navigator.onLine status */
  isOnline: boolean;
  /** Whether the API server responded to last ping (null = not checked yet) */
  isServerReachable: boolean | null;
  /** True briefly after transitioning from offline → online */
  wasOffline: boolean;
}

/** How often to ping the server when online (ms) */
const PING_INTERVAL = 60_000; // 1 minute

/** How long to wait for ping response before considering server unreachable */
const PING_TIMEOUT = 5_000;

export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isServerReachable, setIsServerReachable] = useState<boolean | null>(null);
  const [wasOffline, setWasOffline] = useState(false);
  const wasOfflineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevOnline = useRef(navigator.onLine);

  // Ping the API server to verify real connectivity
  const pingServer = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT);

      // Use a lightweight endpoint — just check if we get any response
      const res = await fetch(`${API_BASE}/health`, {
        method: "GET",
        signal: controller.signal,
        cache: "no-store",
      });
      clearTimeout(timeout);
      setIsServerReachable(res.ok || res.status < 500);
    } catch {
      setIsServerReachable(false);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);

      // If we were previously offline, mark wasOffline briefly
      if (!prevOnline.current) {
        setWasOffline(true);
        // Clear the "was offline" flag after 5 seconds
        if (wasOfflineTimerRef.current) clearTimeout(wasOfflineTimerRef.current);
        wasOfflineTimerRef.current = setTimeout(() => setWasOffline(false), 5000);

        // Ping server immediately to verify
        pingServer();
      }

      prevOnline.current = true;
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsServerReachable(false);
      prevOnline.current = false;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial ping
    if (navigator.onLine) {
      pingServer();
    }

    // Periodic ping when online
    const interval = setInterval(() => {
      if (navigator.onLine) {
        pingServer();
      }
    }, PING_INTERVAL);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
      if (wasOfflineTimerRef.current) clearTimeout(wasOfflineTimerRef.current);
    };
  }, [pingServer]);

  return { isOnline, isServerReachable, wasOffline };
}
